import { IPromise } from 'angular'

import { trackError, trackUserEvent } from '@vend/utilities'
import { $q, $timeout } from '../../ng-compat/imports'
import { WEB_REGISTER } from '../../components/web-register.constant'

export interface SearchResult {
  id: string
  type: string
  exactMatch: boolean
}

const TIMEOUT = 10000

/**
 * A search source for an autocomplete search field. For use with an AutocompleteManager and one or more
 * AutocompleteResultFormatters.
 *
 * @class AutocompleteSource
 * @module webregister.factories.AutocompleteSource
 * @constructor
 *
 * @param {Object} options
 *
 *        @param {String} options.name
 *               A unique name to identify this search source in log messages etc
 *
 *        @param {Array<String>} options.types
 *               Array of types (e.g. 'customer', 'product') that this search source provides results for
 *
 *        @param {Function} options.search
 *               The function that will do the searching - will be invoked with the search query and should produce
 *               an array of result objects that contain at a minimum `id` and `type` properties
 *
 *        @param {Function} [options.whenSyncComplete]
 *               Optional function to determine when the search source has completed syncing (e.g. if it needs to
 *               build an index over a large dataset) - can return a promise.
 */
export class AutocompleteSource {
  private _search: $TSFixMe
  private _whenSyncComplete: $TSFixMe
  private _whenSynced: $TSFixMe
  public name: string
  public types: string[]

  constructor({
    name,
    types,
    search,
    whenSyncComplete,
  }: {
    name: string
    types: string[]
    search: (query: string) => IPromise<SearchResult[]>
    whenSyncComplete?: $TSFixMeFunction
  }) {
    this.name = name
    this.types = types

    this._search = search
    this._whenSyncComplete = whenSyncComplete || function () {}
  }

  /**
   * Initialise the search source.
   *
   * @method init
   *
   * @return {Promise<AutocompleteSource>} A promise that resolves to this search source once init is complete
   */
  public init() {
    return $q.resolve(this)
  }

  /**
   * Wait for sync of the search source to be complete.
   *
   * @method whenSyncComplete
   *
   * @return {Promise<AutocompleteSource>} A promise that resolves to this search source once sync is complete
   */
  public whenSyncComplete() {
    if (!this._whenSynced) {
      this._whenSynced = $q.resolve(this._whenSyncComplete()).then(() => this)
    }

    return this._whenSynced
  }

  /**
   * Search for results to the given query.
   *
   * @method search
   *
   * @param  {String} query
   *         The query to search for
   *
   * @return {Promise<Array>} A promise resolved with the applicable results or an empty array if the source timed out
   *         or encountered an error
   */
  public search(query: string) {
    return $q(resolve => {
      let resolved = false

      const timeout = $timeout(() => {
        trackUserEvent(WEB_REGISTER.APPLICATION_NAME, 'Search Query Timeout', {
          query,
          searchSource: this.name,
          timeout: TIMEOUT,
        })

        doResolve()
      }, TIMEOUT)
      // $timeout.cancel() rejects the timeout promise with "canceled" — suppress to avoid unhandled rejection
      timeout.catch(() => {})

      $q.resolve(this._search(query)).then(
        results => doResolve(results),
        error => {
          trackError(`[Autocomplete] Error searching ${this.name}`, {
            data: { error, query },
          })
          doResolve()
        }
      )

      function doResolve(results = []) {
        if (!resolved) {
          resolved = true
          $timeout.cancel(timeout)
          resolve(results)
        }
      }
    })
  }
}
